import React, { useState, useEffect, useRef } from 'react';
import { X, Eye, ChevronRight, Mail, ArrowLeft, RefreshCw, ShieldCheck, KeyRound, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { 
    sendSignupOtp, 
    verifySignupOtp, 
    resendSignupOtp,
    sendForgotPasswordOtp,
    resetPasswordWithOtp,
    resendForgotPasswordOtp
} from '../services/api';

const StudentLogin = ({
    authModalOpen,
    setAuthModalOpen,
    activeAuthTab,
    setActiveAuthTab,
    loginForm,
    setLoginForm,
    signupForm,
    setSignupForm,
    authError,
    setAuthError,
    showPassword,
    setShowPassword,
    handleLogin,
    _handleSignup
}) => {
    const { login: contextLogin } = useAuth();

    // Sign Up OTP Flow States
    const [otpStep, setOtpStep] = useState('form'); // 'form' | 'otp'
    const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
    const [otpLoading, setOtpLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(60);
    const [canResend, setCanResend] = useState(false);

    // Forgot Password Flow States
    const [loginMode, setLoginMode] = useState('login'); // 'login' | 'forgot_email' | 'forgot_otp_reset'
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotOtpDigits, setForgotOtpDigits] = useState(['', '', '', '', '', '']);
    const [forgotNewPassword, setForgotNewPassword] = useState('');
    const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
    const [forgotShowPassword, setForgotShowPassword] = useState(false);
    const [forgotShowConfirm, setForgotShowConfirm] = useState(false);
    const [forgotTimer, setForgotTimer] = useState(60);
    const [forgotCanResend, setForgotCanResend] = useState(false);
    const [forgotSuccessMsg, setForgotSuccessMsg] = useState('');

    const signupInputRefs = useRef([]);
    const forgotInputRefs = useRef([]);

    // Reset states when modal opens/closes or switches tabs
    useEffect(() => {
        if (!authModalOpen) {
            setOtpStep('form');
            setLoginMode('login');
            setOtpDigits(['', '', '', '', '', '']);
            setForgotOtpDigits(['', '', '', '', '', '']);
            setForgotSuccessMsg('');
            if (setAuthError) setAuthError('');
        }
    }, [authModalOpen, setAuthError]);

    useEffect(() => {
        if (activeAuthTab === 'login') {
            setOtpStep('form');
        } else {
            setLoginMode('login');
        }
        if (setAuthError) setAuthError('');
    }, [activeAuthTab, setAuthError]);

    // Sign Up Resend Timer
    useEffect(() => {
        let interval = null;
        if (otpStep === 'otp' && resendTimer > 0) {
            setCanResend(false);
            interval = setInterval(() => {
                setResendTimer(prev => prev - 1);
            }, 1000);
        } else if (resendTimer === 0) {
            setCanResend(true);
        }
        return () => clearInterval(interval);
    }, [otpStep, resendTimer]);

    // Forgot Password Resend Timer
    useEffect(() => {
        let interval = null;
        if (loginMode === 'forgot_otp_reset' && forgotTimer > 0) {
            setForgotCanResend(false);
            interval = setInterval(() => {
                setForgotTimer(prev => prev - 1);
            }, 1000);
        } else if (forgotTimer === 0) {
            setForgotCanResend(true);
        }
        return () => clearInterval(interval);
    }, [loginMode, forgotTimer]);

    // Google GSI script load
    useEffect(() => {
        if (authModalOpen && !document.getElementById('google-gsi-script')) {
            const script = document.createElement('script');
            script.id = 'google-gsi-script';
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        }
    }, [authModalOpen]);

    // ----------------------------------------------------
    // SIGN UP OTP HANDLERS
    // ----------------------------------------------------
    const onSignupFormSubmit = async (e) => {
        e.preventDefault();
        if (setAuthError) setAuthError('');

        if (signupForm.password !== signupForm.confirmPassword) {
            if (setAuthError) setAuthError("Passwords do not match.");
            return;
        }

        setOtpLoading(true);
        try {
            const res = await sendSignupOtp({
                name: signupForm.name,
                email: signupForm.email,
                phone: signupForm.phone,
                password: signupForm.password,
                role: 'student'
            });

            if (res.success) {
                setOtpStep('otp');
                setResendTimer(60);
                setCanResend(false);
                setTimeout(() => {
                    if (signupInputRefs.current[0]) signupInputRefs.current[0].focus();
                }, 150);
            } else {
                if (setAuthError) setAuthError(res.message || "Failed to send verification code.");
            }
        } catch (err) {
            console.error("Send OTP Error:", err);
            if (setAuthError) setAuthError("Network error. Failed to send verification OTP.");
        } finally {
            setOtpLoading(false);
        }
    };

    const handleSignupOtpBoxChange = (index, value) => {
        if (/[^0-9]/.test(value) && value !== '') return;
        const newDigits = [...otpDigits];
        newDigits[index] = value.slice(-1);
        setOtpDigits(newDigits);
        if (value && index < 5 && signupInputRefs.current[index + 1]) {
            signupInputRefs.current[index + 1].focus();
        }
    };

    const handleSignupOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otpDigits[index] && index > 0 && signupInputRefs.current[index - 1]) {
            signupInputRefs.current[index - 1].focus();
        }
    };

    const handleSignupOtpPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').trim();
        if (/^\d{6}$/.test(pastedData)) {
            setOtpDigits(pastedData.split(''));
            if (signupInputRefs.current[5]) signupInputRefs.current[5].focus();
        }
    };

    const onVerifySignupOtpSubmit = async (e) => {
        e.preventDefault();
        const code = otpDigits.join('');
        if (code.length < 6) {
            if (setAuthError) setAuthError("Please enter all 6 digits of the verification code.");
            return;
        }

        setOtpLoading(true);
        if (setAuthError) setAuthError('');

        try {
            const res = await verifySignupOtp({
                email: signupForm.email,
                otp_code: code
            });

            if (res.success && res.token && res.user) {
                contextLogin(res.user, res.token);
                setAuthModalOpen(false);
            } else {
                if (setAuthError) setAuthError(res.message || "Invalid or expired verification code.");
            }
        } catch (err) {
            console.error("Verify OTP Error:", err);
            if (setAuthError) setAuthError("Verification failed. Please check your code and try again.");
        } finally {
            setOtpLoading(false);
        }
    };

    const handleResendSignupOtp = async () => {
        if (!canResend || otpLoading) return;
        setOtpLoading(true);
        if (setAuthError) setAuthError('');

        try {
            const res = await resendSignupOtp(signupForm.email);
            if (res.success) {
                setResendTimer(60);
                setCanResend(false);
                setOtpDigits(['', '', '', '', '', '']);
                if (signupInputRefs.current[0]) signupInputRefs.current[0].focus();
            } else {
                if (setAuthError) setAuthError(res.message || "Failed to resend code.");
            }
        } catch (err) {
            console.error("Resend OTP Error:", err);
            if (setAuthError) setAuthError("Failed to resend code. Please try again.");
        } finally {
            setOtpLoading(false);
        }
    };

    // ----------------------------------------------------
    // FORGOT PASSWORD OTP HANDLERS
    // ----------------------------------------------------
    const onSendForgotOtp = async (e) => {
        e.preventDefault();
        if (setAuthError) setAuthError('');
        setForgotSuccessMsg('');

        if (!forgotEmail.trim()) {
            if (setAuthError) setAuthError("Please enter your registered email address.");
            return;
        }

        setOtpLoading(true);
        try {
            const res = await sendForgotPasswordOtp(forgotEmail);
            if (res.success) {
                setLoginMode('forgot_otp_reset');
                setForgotTimer(60);
                setForgotCanResend(false);
                setTimeout(() => {
                    if (forgotInputRefs.current[0]) forgotInputRefs.current[0].focus();
                }, 150);
            } else {
                if (setAuthError) setAuthError(res.message || "Failed to send reset code.");
            }
        } catch (err) {
            console.error("Forgot Password OTP Error:", err);
            if (setAuthError) setAuthError("Failed to send reset code. Please check your email.");
        } finally {
            setOtpLoading(false);
        }
    };

    const handleForgotOtpBoxChange = (index, value) => {
        if (/[^0-9]/.test(value) && value !== '') return;
        const newDigits = [...forgotOtpDigits];
        newDigits[index] = value.slice(-1);
        setForgotOtpDigits(newDigits);
        if (value && index < 5 && forgotInputRefs.current[index + 1]) {
            forgotInputRefs.current[index + 1].focus();
        }
    };

    const handleForgotOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !forgotOtpDigits[index] && index > 0 && forgotInputRefs.current[index - 1]) {
            forgotInputRefs.current[index - 1].focus();
        }
    };

    const handleForgotOtpPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').trim();
        if (/^\d{6}$/.test(pastedData)) {
            setForgotOtpDigits(pastedData.split(''));
            if (forgotInputRefs.current[5]) forgotInputRefs.current[5].focus();
        }
    };

    const onResetPasswordSubmit = async (e) => {
        e.preventDefault();
        if (setAuthError) setAuthError('');

        const code = forgotOtpDigits.join('');
        if (code.length < 6) {
            if (setAuthError) setAuthError("Please enter all 6 digits of the reset code.");
            return;
        }

        if (forgotNewPassword !== forgotConfirmPassword) {
            if (setAuthError) setAuthError("Passwords do not match.");
            return;
        }

        if (forgotNewPassword.length < 6) {
            if (setAuthError) setAuthError("New password must be at least 6 characters.");
            return;
        }

        setOtpLoading(true);
        try {
            const res = await resetPasswordWithOtp({
                email: forgotEmail,
                otp_code: code,
                new_password: forgotNewPassword
            });

            if (res.success) {
                setLoginForm(prev => ({ ...prev, email: forgotEmail, password: '' }));
                setLoginMode('login');
                setForgotSuccessMsg("Password reset successful! Please sign in with your new password.");
                setForgotOtpDigits(['', '', '', '', '', '']);
                setForgotNewPassword('');
                setForgotConfirmPassword('');
            } else {
                if (setAuthError) setAuthError(res.message || "Password reset failed. Check code & try again.");
            }
        } catch (err) {
            console.error("Reset Password Submit Error:", err);
            if (setAuthError) setAuthError("Failed to reset password. Please try again.");
        } finally {
            setOtpLoading(false);
        }
    };

    const handleResendForgotOtp = async () => {
        if (!forgotCanResend || otpLoading) return;
        setOtpLoading(true);
        if (setAuthError) setAuthError('');

        try {
            const res = await resendForgotPasswordOtp(forgotEmail);
            if (res.success) {
                setForgotTimer(60);
                setForgotCanResend(false);
                setForgotOtpDigits(['', '', '', '', '', '']);
                if (forgotInputRefs.current[0]) forgotInputRefs.current[0].focus();
            } else {
                if (setAuthError) setAuthError(res.message || "Failed to resend reset code.");
            }
        } catch (err) {
            console.error("Resend Forgot OTP Error:", err);
            if (setAuthError) setAuthError("Failed to resend code. Please try again.");
        } finally {
            setOtpLoading(false);
        }
    };

    return (
        <>
            {/* Auth Modal */}
            {authModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setAuthModalOpen(false)} />
                    <div className="relative bg-white rounded-2xl p-5 sm:p-8 max-w-md w-full max-h-[90vh] my-auto overflow-y-auto shadow-2xl border border-gray-100">
                        <button
                            onClick={() => setAuthModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                        >
                            <X size={22} />
                        </button>

                        <div className="text-center mb-5 sm:mb-6">
                            <h2 className="text-xl sm:text-2xl font-bold mb-1 text-gray-900">Welcome to E-Learning Hub</h2>
                            <p className="text-xs sm:text-sm text-gray-600">Sign in to your account or create a new one</p>
                        </div>

                        {/* Navigation Tabs */}
                        <div className="flex gap-2 sm:gap-4 mb-5 sm:mb-6">
                            <button
                                onClick={() => { setActiveAuthTab('login'); setLoginMode('login'); }}
                                className={`flex-1 py-2.5 sm:py-3 rounded-xl font-bold text-sm transition-all ${activeAuthTab === 'login'
                                    ? 'bg-emerald-600 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                Sign In
                            </button>
                            <button
                                onClick={() => { setActiveAuthTab('signup'); setOtpStep('form'); }}
                                className={`flex-1 py-2.5 sm:py-3 rounded-xl font-bold text-sm transition-all ${activeAuthTab === 'signup'
                                    ? 'bg-emerald-600 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                Sign Up
                            </button>
                        </div>

                        {/* SIGN IN TAB CONTENT */}
                        {activeAuthTab === 'login' ? (
                            loginMode === 'login' ? (
                                /* SIGN IN FORM */
                                <form onSubmit={handleLogin} className="space-y-4">
                                    {forgotSuccessMsg && (
                                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-xs sm:text-sm flex items-start gap-2">
                                            <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                                            <p className="font-bold">{forgotSuccessMsg}</p>
                                        </div>
                                    )}

                                    {authError && (
                                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs sm:text-sm">
                                            <p className="font-medium">{authError}</p>
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-xs sm:text-sm font-semibold mb-1.5 text-gray-700">Email</label>
                                        <input
                                            type="email"
                                            placeholder="Enter your email"
                                            value={loginForm.email}
                                            onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                                            className="w-full px-3.5 py-2.5 sm:py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs sm:text-sm font-semibold mb-1.5 text-gray-700">Password</label>
                                        <div className="relative">
                                            <input
                                                type={showPassword.login ? 'text' : 'password'}
                                                placeholder="Enter your password"
                                                value={loginForm.password}
                                                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                                                className="w-full px-3.5 pr-10 py-2.5 sm:py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword({ ...showPassword, login: !showPassword.login })}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <Eye size={18} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between text-xs sm:text-sm">
                                        <label className="flex items-center gap-2 cursor-pointer text-gray-600">
                                            <input type="checkbox" className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                                            <span>Remember me</span>
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setLoginMode('forgot_email');
                                                setForgotEmail(loginForm.email || '');
                                                if (setAuthError) setAuthError('');
                                                setForgotSuccessMsg('');
                                            }}
                                            className="text-emerald-600 font-semibold hover:underline bg-transparent border-0 p-0 cursor-pointer"
                                        >
                                            Forgot password?
                                        </button>
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full bg-emerald-600 text-white py-3 rounded-xl font-bold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 text-sm shadow-md"
                                    >
                                        Sign In <ChevronRight size={18} />
                                    </button>
                                </form>
                            ) : loginMode === 'forgot_email' ? (
                                /* FORGOT PASSWORD STEP 1: ENTER EMAIL */
                                <form onSubmit={onSendForgotOtp} className="space-y-4 animate-in fade-in duration-200">
                                    <button
                                        type="button"
                                        onClick={() => setLoginMode('login')}
                                        className="flex items-center gap-1.5 text-xs text-emerald-600 font-bold hover:underline mb-1"
                                    >
                                        <ArrowLeft size={14} /> Back to Sign In
                                    </button>

                                    <div className="text-center space-y-1">
                                        <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-2">
                                            <KeyRound size={20} />
                                        </div>
                                        <h3 className="font-bold text-gray-900 text-base">Reset Your Password</h3>
                                        <p className="text-xs text-gray-500">
                                            Enter your registered email address to receive a 6-digit password reset code.
                                        </p>
                                    </div>

                                    {authError && (
                                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-medium">
                                            {authError}
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-xs sm:text-sm font-semibold mb-1 text-gray-700">Email Address</label>
                                        <input
                                            type="email"
                                            placeholder="Enter your registered email"
                                            value={forgotEmail}
                                            onChange={(e) => setForgotEmail(e.target.value)}
                                            className="w-full px-3.5 py-2.5 sm:py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                                            required
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={otpLoading}
                                        className="w-full bg-emerald-600 text-white py-3 rounded-xl font-bold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 text-sm shadow-md disabled:opacity-60"
                                    >
                                        {otpLoading ? (
                                            <>
                                                <RefreshCw size={16} className="animate-spin" /> Sending Reset Code...
                                            </>
                                        ) : (
                                            <>
                                                Send Reset Code <ChevronRight size={18} />
                                            </>
                                        )}
                                    </button>
                                </form>
                            ) : (
                                /* FORGOT PASSWORD STEP 2: ENTER OTP & NEW PASSWORD */
                                <form onSubmit={onResetPasswordSubmit} className="space-y-3.5 animate-in fade-in duration-200">
                                    <button
                                        type="button"
                                        onClick={() => setLoginMode('forgot_email')}
                                        className="flex items-center gap-1.5 text-xs text-emerald-600 font-bold hover:underline mb-1"
                                    >
                                        <ArrowLeft size={14} /> Back to Email Entry
                                    </button>

                                    <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-center space-y-1">
                                        <div className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center mx-auto">
                                            <Mail size={16} />
                                        </div>
                                        <h3 className="font-bold text-gray-900 text-xs sm:text-sm">Verification Code Sent</h3>
                                        <p className="text-[11px] text-gray-600">
                                            We sent a code to <strong className="text-emerald-700">{forgotEmail}</strong>
                                        </p>
                                    </div>

                                    {authError && (
                                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-medium">
                                            {authError}
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-[11px] font-bold text-gray-700 text-center mb-1.5 uppercase tracking-wide">
                                            6-Digit Reset Code
                                        </label>
                                        <div className="flex justify-center gap-1.5 sm:gap-2" onPaste={handleForgotOtpPaste}>
                                            {forgotOtpDigits.map((digit, idx) => (
                                                <input
                                                    key={idx}
                                                    ref={(el) => (forgotInputRefs.current[idx] = el)}
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={1}
                                                    value={digit}
                                                    onChange={(e) => handleForgotOtpBoxChange(idx, e.target.value)}
                                                    onKeyDown={(e) => handleForgotOtpKeyDown(idx, e)}
                                                    className="w-9 sm:w-10 h-11 text-center text-lg font-black border-2 border-gray-200 rounded-xl focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20 bg-gray-50 transition-all"
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold mb-1 text-gray-700">New Password</label>
                                        <div className="relative">
                                            <input
                                                type={forgotShowPassword ? 'text' : 'password'}
                                                placeholder="Enter new password"
                                                value={forgotNewPassword}
                                                onChange={(e) => setForgotNewPassword(e.target.value)}
                                                className="w-full px-3.5 pr-10 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setForgotShowPassword(!forgotShowPassword)}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <Eye size={16} />
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold mb-1 text-gray-700">Confirm New Password</label>
                                        <div className="relative">
                                            <input
                                                type={forgotShowConfirm ? 'text' : 'password'}
                                                placeholder="Confirm new password"
                                                value={forgotConfirmPassword}
                                                onChange={(e) => setForgotConfirmPassword(e.target.value)}
                                                className="w-full px-3.5 pr-10 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setForgotShowConfirm(!forgotShowConfirm)}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <Eye size={16} />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between text-xs pt-1">
                                        <span className="text-gray-500 text-[11px]">
                                            {forgotTimer > 0 ? (
                                                <>Resend code in <strong className="text-emerald-600">{forgotTimer}s</strong></>
                                            ) : (
                                                "Didn't receive code?"
                                            )}
                                        </span>
                                        <button
                                            type="button"
                                            disabled={!forgotCanResend || otpLoading}
                                            onClick={handleResendForgotOtp}
                                            className="font-bold text-emerald-600 hover:underline text-xs disabled:opacity-40 disabled:no-underline"
                                        >
                                            Resend Code
                                        </button>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={otpLoading || forgotOtpDigits.join('').length < 6}
                                        className="w-full bg-emerald-600 text-white py-3 rounded-xl font-bold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 text-sm shadow-md disabled:opacity-50"
                                    >
                                        {otpLoading ? (
                                            <>
                                                <RefreshCw size={16} className="animate-spin" /> Updating Password...
                                            </>
                                        ) : (
                                            <>
                                                <ShieldCheck size={18} /> Update Password & Sign In
                                            </>
                                        )}
                                    </button>
                                </form>
                            )
                        ) : otpStep === 'form' ? (
                            /* SIGN UP TAB STEP 1: INITIAL SIGN UP FORM */
                            <form onSubmit={onSignupFormSubmit} className="space-y-3.5">
                                {authError && (
                                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs sm:text-sm">
                                        <p className="font-medium">{authError}</p>
                                        {authError.toLowerCase().includes('already exists') && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setActiveAuthTab('login');
                                                    setLoginMode('login');
                                                    if (signupForm?.email) {
                                                        setLoginForm(prev => ({ ...prev, email: signupForm.email }));
                                                    }
                                                }}
                                                className="mt-1.5 font-bold text-emerald-700 hover:text-emerald-800 underline block"
                                            >
                                                Switch to Sign In →
                                            </button>
                                        )}
                                    </div>
                                )}
                                <div>
                                    <label className="block text-xs sm:text-sm font-semibold mb-1 text-gray-700">Full Name</label>
                                    <input
                                        type="text"
                                        placeholder="Enter your full name"
                                        value={signupForm.name}
                                        onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })}
                                        className="w-full px-3.5 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs sm:text-sm font-semibold mb-1 text-gray-700">Email</label>
                                    <input
                                        type="email"
                                        placeholder="Enter your email"
                                        value={signupForm.email}
                                        onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
                                        className="w-full px-3.5 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs sm:text-sm font-semibold mb-1 text-gray-700">Mobile Number</label>
                                    <input
                                        type="tel"
                                        placeholder="Enter 10-digit mobile number"
                                        value={signupForm.phone || ''}
                                        onChange={(e) => setSignupForm({ ...signupForm, phone: e.target.value })}
                                        className="w-full px-3.5 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                                        required
                                    />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                    <div>
                                        <label className="block text-xs sm:text-sm font-semibold mb-1 text-gray-700">Password</label>
                                        <div className="relative">
                                            <input
                                                type={showPassword.signup ? 'text' : 'password'}
                                                placeholder="Create password"
                                                value={signupForm.password}
                                                onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })}
                                                className="w-full px-3.5 pr-10 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword({ ...showPassword, signup: !showPassword.signup })}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <Eye size={18} />
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs sm:text-sm font-semibold mb-1 text-gray-700">Confirm Password</label>
                                        <div className="relative">
                                            <input
                                                type={showPassword.confirm ? 'text' : 'password'}
                                                placeholder="Confirm password"
                                                value={signupForm.confirmPassword}
                                                onChange={(e) => setSignupForm({ ...signupForm, confirmPassword: e.target.value })}
                                                className="w-full px-3.5 pr-10 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword({ ...showPassword, confirm: !showPassword.confirm })}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <Eye size={18} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <label className="flex items-start gap-2 cursor-pointer text-gray-600 py-1">
                                    <input type="checkbox" className="mt-0.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" required />
                                    <span className="text-xs">I agree to the Terms of Service and Privacy Policy</span>
                                </label>
                                <button
                                    type="submit"
                                    disabled={otpLoading}
                                    className="w-full bg-emerald-600 text-white py-3 rounded-xl font-bold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 text-sm shadow-md disabled:opacity-60"
                                >
                                    {otpLoading ? (
                                        <>
                                            <RefreshCw size={16} className="animate-spin" /> Sending Verification Code...
                                        </>
                                    ) : (
                                        <>
                                            Create Account <ChevronRight size={18} />
                                        </>
                                    )}
                                </button>
                            </form>
                        ) : (
                            /* SIGN UP TAB STEP 2: 6-DIGIT EMAIL OTP VERIFICATION STEP */
                            <form onSubmit={onVerifySignupOtpSubmit} className="space-y-4 animate-in fade-in duration-200">
                                <button
                                    type="button"
                                    onClick={() => setOtpStep('form')}
                                    className="flex items-center gap-1.5 text-xs text-emerald-600 font-bold hover:underline mb-1"
                                >
                                    <ArrowLeft size={14} /> Back to Sign Up Form
                                </button>

                                <div className="p-4 bg-emerald-50/80 border border-emerald-200 rounded-2xl text-center space-y-1.5">
                                    <div className="w-10 h-10 bg-emerald-600 text-white rounded-full flex items-center justify-center mx-auto shadow-xs">
                                        <Mail size={20} />
                                    </div>
                                    <h3 className="font-bold text-gray-900 text-sm">Verify Your Email</h3>
                                    <p className="text-xs text-gray-600">
                                        We sent a 6-digit verification code to <strong className="text-emerald-700">{signupForm.email}</strong>
                                    </p>
                                </div>

                                {authError && (
                                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-medium">
                                        {authError}
                                    </div>
                                )}

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 text-center mb-2 uppercase tracking-wide">
                                        Enter 6-Digit Code
                                    </label>
                                    <div className="flex justify-center gap-2" onPaste={handleSignupOtpPaste}>
                                        {otpDigits.map((digit, idx) => (
                                            <input
                                                key={idx}
                                                ref={(el) => (signupInputRefs.current[idx] = el)}
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={1}
                                                value={digit}
                                                onChange={(e) => handleSignupOtpBoxChange(idx, e.target.value)}
                                                onKeyDown={(e) => handleSignupOtpKeyDown(idx, e)}
                                                className="w-10 h-12 text-center text-lg font-black border-2 border-gray-200 rounded-xl focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20 bg-gray-50 transition-all"
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="flex items-center justify-between text-xs pt-1">
                                    <span className="text-gray-500">
                                        {resendTimer > 0 ? (
                                            <>Resend code in <strong className="text-emerald-600">{resendTimer}s</strong></>
                                        ) : (
                                            "Didn't receive code?"
                                        )}
                                    </span>
                                    <button
                                        type="button"
                                        disabled={!canResend || otpLoading}
                                        onClick={handleResendSignupOtp}
                                        className="font-bold text-emerald-600 hover:underline disabled:opacity-40 disabled:no-underline"
                                    >
                                        Resend Code
                                    </button>
                                </div>

                                <button
                                    type="submit"
                                    disabled={otpLoading || otpDigits.join('').length < 6}
                                    className="w-full bg-emerald-600 text-white py-3 rounded-xl font-bold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 text-sm shadow-md disabled:opacity-50"
                                >
                                    {otpLoading ? (
                                        <>
                                            <RefreshCw size={16} className="animate-spin" /> Verifying...
                                        </>
                                    ) : (
                                        <>
                                            <ShieldCheck size={18} /> Verify & Complete Registration
                                        </>
                                    )}
                                </button>
                            </form>
                        )}

                        <div className="mt-4 pt-3 border-t border-gray-100 text-center text-xs sm:text-sm text-gray-600">
                            {activeAuthTab === 'login' ? (
                                <p>
                                    Don't have an account?{' '}
                                    <button
                                        type="button"
                                        onClick={() => { setActiveAuthTab('signup'); setOtpStep('form'); }}
                                        className="text-emerald-600 font-bold hover:underline"
                                    >
                                        Sign up
                                    </button>
                                </p>
                            ) : (
                                <p>
                                    Already have an account?{' '}
                                    <button
                                        type="button"
                                        onClick={() => { setActiveAuthTab('login'); setLoginMode('login'); }}
                                        className="text-emerald-600 font-bold hover:underline"
                                    >
                                        Sign in
                                    </button>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default StudentLogin;
